import { LIMIT } from '@/constants';
import {
  fetchCheckinRewardsConfig,
  fetchConsecutiveDays,
  fetchCustomerCheckinRecords,
  fetchCustomerPrizeRecords,
  fetchGetPointsProducts,
  fetchPointsHistory,
} from '@/services/apis';
import { LoadMoreStatus, Sort } from '@/types';
import moment from 'moment';
import { defineStore } from 'pinia';

interface State {
  /** 积分商品列表数据配置 */
  productConfig: {
    list: AnyObject[];
    /** 列表加载状态 */
    loadStatus: LoadMoreStatus;
    /** 列表请求选项 */
    options: {
      skip: number;
    };
  };
  /** 积分明细数据配置 */
  detailedConfig: {
    list: AnyObject[];
    /** 列表加载状态 */
    loadStatus: LoadMoreStatus;
    /** 列表请求选项 */
    options: {
      skip: number;
      start: string;
      end: string;
      symbolType: '';
    };
  };
  /** 签到数据相关配置 */
  signinConfig: {
    // 配置数据
    data: AnyObject | null;
    // 连签数据
    consecutives: { consecutiveDays: number; isCheckedIn: boolean };
    // 签到记录
    checkinRecords: AnyObject[];
    // 连签奖励
    prizeRecords: { eligiblePrizes: AnyObject[]; receivedPrizeRecords: AnyObject[] } | null;

    year: number;
    month: number;
  };
}

export const useIntegralSigninStore = defineStore('integralSignin', {
  state: (): State => ({
    productConfig: {
      list: [],
      loadStatus: LoadMoreStatus.loading,
      options: {
        skip: 0,
      },
    },
    detailedConfig: {
      list: [],
      loadStatus: LoadMoreStatus.loading,
      options: {
        skip: 0,
        start: '',
        end: '',
        symbolType: '',
      },
    },
    signinConfig: {
      data: null,
      consecutives: { consecutiveDays: 0, isCheckedIn: false },
      checkinRecords: [],
      prizeRecords: null,
      year: new Date().getFullYear(),
      month: new Date().getMonth(),
    },
  }),

  getters: {},

  actions: {
    async getProductList(isAllowExchange: boolean, { skip = 0, take = 10 }, refresh?: boolean) {
      const res = await fetchGetPointsProducts({
        isAllowExchange,
        options: { skip: skip * LIMIT, take, sort: { updatedAt: Sort.DESC } },
      });

      if (res?.items) {
        if (res.items.length < LIMIT) {
          this.productConfig.loadStatus = LoadMoreStatus.noMore;
        } else {
          this.productConfig.loadStatus = LoadMoreStatus.more;
        }
        if (refresh) {
          this.productConfig.list = res.items;
        } else {
          this.productConfig.list = [...this.productConfig.list, ...res.items];
        }
      }
      return this.productConfig.list;
    },
    async getDetailedList({ skip = 0, start = '', end = '', symbolType = '' }, refresh?: boolean) {
      const params = {
        skip: skip * LIMIT * 5,
        take: LIMIT * 5,
        sort: {
          createdAt: Sort.DESC,
        },
        filter: {
          createdAt: {
            ...(start && { between: { start, end } }),
          },
          ...(symbolType && { symbolType: { eq: symbolType } }),
        },
      };

      console.log(`params`, JSON.stringify(params));

      const res = await fetchPointsHistory(params);

      if (res?.items) {
        if (res.items.length < LIMIT) {
          this.detailedConfig.loadStatus = LoadMoreStatus.noMore;
        } else {
          this.detailedConfig.loadStatus = LoadMoreStatus.more;
        }
        if (refresh) {
          this.detailedConfig.list = res.items;
        } else {
          this.detailedConfig.list = [...this.detailedConfig.list, ...res.items];
        }
      }
      console.log(`this.detailedConfig.list`, this.detailedConfig.list);

      return this.detailedConfig.list;
    },
    async getCheckinRewardsConfig(refresh = false) {
      if (!this.signinConfig.data || refresh) {
        const res = await fetchCheckinRewardsConfig();
        console.log(`签到配置：`, res);
        if (res) {
          this.signinConfig.data = res;
        }
        return this.signinConfig.data;
      }
    },
    async getConsecutiveDays(refresh = false) {
      if (!this.signinConfig.consecutives || refresh) {
        const res = await fetchConsecutiveDays();
        console.log(`连签天数：`, res);
        if (res) {
          this.signinConfig.consecutives = res;
        }
        return this.signinConfig.consecutives;
      }
    },
    async getCheckinRecords(year: number, month: number, refresh = false) {
      if (!this.signinConfig.checkinRecords.length || refresh) {
        const res = await fetchCustomerCheckinRecords({ year, month });
        if (res) {
          this.signinConfig.checkinRecords = res.map((it: AnyObject) => {
            it._date = moment(it.checkinDate).date().toString();
            return it;
          });
        }
        console.log(`签到记录：`, this.signinConfig.checkinRecords);
        return this.signinConfig.checkinRecords;
      }
    },
    async getPrizeRecords(refresh = false) {
      if (!this.signinConfig.prizeRecords || refresh) {
        const res = await fetchCustomerPrizeRecords();
        console.log(`连签奖励：`, res);
        if (res) {
          this.signinConfig.prizeRecords = res;
        }
        return this.signinConfig.prizeRecords;
      }
    },
    async reupdateFetchFlows() {
      await this.getConsecutiveDays(true);

      await this.getPrizeRecords(true);

      const year = new Date().getFullYear();
      const month = new Date().getMonth();
      this.signinConfig.year = year;
      this.signinConfig.month = month;
      await this.getCheckinRecords(year, month, true);
    },
  },
});
